/* eslint class-methods-use-this: 0 */
import _ from "lodash";
import { nanoid } from "nanoid";
import { fromJS } from "immutable";

import ModelFactory from "../models/ModelFactory";
import BlockConverter from "../utils/BlockConverter";
import ObjectUtils from "../utils/ObjectUtils";
import ThemeApplier from "./ThemeApplier";
import WordpressRemover from "../utils/WordpressRemover";
import { applyThemeBG, recursivelyCopySyncedPropsToMobile } from "../utils/SyncUtils";
import ElementUtils from "@/utils/ElementUtils";
import TransformerStore from "@/stores/TransformerStore";
import { gatherProps } from "@/utils/Merge/MergeUtils.mjs";

import AppConstants from "../constants/AppConstants";
import { DEFAULT_MERGE_PROPS } from "../constants/SyncConstants";
import { LAYOUTS } from "./ThemeApplier";
import PROPERTY_MAPS from "@/constants/PropertyMaps";
import DataTypes from "@/constants/DataTypes";
import { ELEMENTS, ELEMENT_ORDER } from "@/constants/EntityConstants";
import {
  BORDER_STYLE,
  BORDER_WIDTH,
  BORDER_COLOR,
} from "@/constants/MergeConstants";
import {
  FORM_FIELD_CHECKBOX_HEIGHTS,
  FORM_FIELD_INPUT_HEIGHTS,
  FORM_FIELD_MULTILINE_HEIGHTS,
  FORM_FIELD_WIDTHS,
} from "@/constants/FormFieldConstants.mjs";
import { createDefaultFormStyles, hasAllFormStyles } from "@/utils/ThemeUtils";

/**
 * @typedef {import("@/entities/SiteDesign").default} SiteDesign
 * @typedef {import("@/entities/Block").default} Block
 * @typedef {import("@/entities/ElementContainer").default} ElementContainer
 * @typedef {import("immutable").Map} ImmutableMap
 * @typedef {import("@/types/data/DesignTheme.mjs").DesignTheme} DesignTheme
 */

const {
  ALIGN_CENTER,
  CANVAS_SET_BLOCK_CONTENT_SEPARATOR,
  ID_LENGTH,
  DESIGN_TYPE_BLOCK,
  DESIGN_TYPE_SITE_BLOCK,
  DESIGN_TYPE_PAGE,
  DESIGN_ITEM_PROP_PARENT_TYPE,
  BLOCK_PROP_PARENT_BLOCK_ID,
  BLOCK_PROP_PARENT_BLOCK,
  DESIGN_ITEM_PROP_NAME,
  DESIGN_TYPE_WP_PAGE,
  LAYOUT_MOBILE,
  LAYOUT_DESKTOP,
  ELEMENT_TYPE_ICON,
  ELEMENT_TYPE_GRAPHIC,
  ELEMENT_TYPE_LINE,
  ELEMENT_TYPE_LOTTIE,
  ELEMENT_TYPE_SOCIAL_GRID,
  ELEMENT_TYPE_SVG,
  ELEMENT_TYPE_TEXT,
  ELEMENT_TYPE_BUTTON,
  ELEMENT_TYPE_FORM_INPUT,
  ELEMENT_TYPE_FORM_SELECT,
  ELEMENT_TYPE_FORM_RADIO,
  ELEMENT_TYPE_FORM_CHECKBOX,
  SITE_ITEM_SITE,
  ELEMENT_ACTION_SHOW,
  ELEMENT_ACTION_HIDE,
  ELEMENT_ACTION_SHOW_SITE_BLOCK,
  ELEMENT_ACTION_HIDE_SITE_BLOCK,
} = AppConstants;

const PROPS_TO_COPY_FROM_SITE_BLOCK = [
  ["z"],
  ["visible"],
  [LAYOUT_MOBILE, "locking"],
  [LAYOUT_DESKTOP, "locking"],
  [LAYOUT_MOBILE, "hide"],
  [LAYOUT_DESKTOP, "hide"],
];

const DEFAULT_BUTTON_MOBILE_WIDTH = 160;
const DEFAULT_BUTTON_DESKTOP_WIDTH = 180;
const DEFAULT_IMAGE_WIDTH_DESKTOP = 220;
const DEFAULT_IMAGE_HEIGHT_DESKTOP = 300;
const DEFAULT_IMAGE_WIDTH_MOBILE = 105;
const DEFAULT_IMAGE_HEIGHT_MOBILE = 140;

class DesignPreparer {
  /**
   * Prepares an item to be added to the current design
   * @param {Object} data
   * @param {SiteDesign} siteDesign the current active design
   * @returns {[Object, boolean]} an object containing the prepared item and any theme information to be added
   */
  prepareDesignItem(data, siteDesign) {
    const targetDesignType = _.get(data, "targetDesignType");
    const itemType = _.get(data, "type");
    const designId = _.get(data, "designId"); // The design that the item is from
    const applyTheme = _.get(data, "applyTheme", false);

    _.set(data, "isCurrentDesign", designId === siteDesign.id);

    const result = {
      item: null,
      theme: null,
      type: targetDesignType,
    };

    let itemInfo;
    let dirty = false;

    switch (itemType) {
      case DESIGN_TYPE_BLOCK:
        itemInfo = this._prepareDesignBlock(data);
        break;
      case DESIGN_TYPE_SITE_BLOCK:
        itemInfo = this._prepareDesignSiteBlock(data);
        break;
      case DESIGN_TYPE_PAGE:
        itemInfo = this._prepareDesignPage(data);
        break;
      case SITE_ITEM_SITE:
        [itemInfo, dirty] = this._prepareSite(data);
        break;
      default:
    }

    if (itemInfo) {
      _.merge(result, itemInfo);
    }

    if (!applyTheme) {
      const [, theme] = recursivelyCopySyncedPropsToMobile(_.get(data, "theme"));

      const themeApplier = new ThemeApplier(theme);

      const applyResult = themeApplier.applyTo(result.item);

      result.item = applyResult.item;
      result.theme = applyResult.theme;
    }

    return [result, dirty];
  }

  /**
   *
   * @param {*} data
   * @returns
   */
  _prepareDesignBlock(data) {
    const result = {
      item: null,
    };

    const block = _.get(data, "item");
    const targetType = _.get(data, "targetDesignType");
    const isCurrentDesign = _.get(data, "isCurrentDesign");

    if (isCurrentDesign) {
      // how the block is going to be added to the current design
      switch (targetType) {
        case DESIGN_TYPE_BLOCK:
          result.item = block;

          break;
        case DESIGN_TYPE_SITE_BLOCK:
          result.item = _.has(block, BLOCK_PROP_PARENT_BLOCK_ID)
            ? this._mergeSiteBlockIntoBlock(block)
            : block;

          _.set(result.item, DESIGN_ITEM_PROP_PARENT_TYPE, SITE_ITEM_SITE);
          break;
        default:
      }
    } else {
      result.item = _.has(block, BLOCK_PROP_PARENT_BLOCK_ID)
        ? this._mergeSiteBlockIntoBlock(block)
        : block;
    }

    return result;
  }

  _mergeSiteBlockIntoBlock(/** Object **/ block) {
    var mergedBlock = block;

    if (_.has(block, BLOCK_PROP_PARENT_BLOCK)) {
      var siteBlock = _.get(block, BLOCK_PROP_PARENT_BLOCK);
      mergedBlock = BlockConverter.toPageBlock(block, siteBlock);
    }

    return mergedBlock;
  }

  /**
   *
   * @param {Object} data
   * @returns
   */
  _prepareDesignSiteBlock(data) {
    const result = {
      item: null,
    };

    const siteBlock = _.get(data, "item");
    const targetType = _.get(data, "targetDesignType");
    const isCurrentDesign = _.get(data, "isCurrentDesign");

    if (isCurrentDesign) {
      switch (targetType) {
        case DESIGN_TYPE_BLOCK:
          result.item = this._configurePageBlockFromSiteBlock(ModelFactory.getBlock(), siteBlock);
          break;
        case DESIGN_TYPE_SITE_BLOCK:
          _.set(siteBlock, DESIGN_ITEM_PROP_PARENT_TYPE, SITE_ITEM_SITE);
          result.item = siteBlock;
          break;
        default:
      }
    } else {
      switch (targetType) {
        case DESIGN_TYPE_BLOCK:
          _.unset(siteBlock, DESIGN_ITEM_PROP_PARENT_TYPE);
          result.item = siteBlock;
          break;
        case DESIGN_TYPE_SITE_BLOCK:
          _.set(siteBlock, DESIGN_ITEM_PROP_PARENT_TYPE, SITE_ITEM_SITE);
          result.item = siteBlock;
          break;
        default:
      }
    }

    return result;
  }

  _configurePageBlockFromSiteBlock(block, siteBlock) {
    _.set(block, BLOCK_PROP_PARENT_BLOCK_ID, siteBlock.id);
    _.set(block, DESIGN_ITEM_PROP_NAME, siteBlock.name);

    var defaultProps = ObjectUtils.pickDeep(siteBlock, PROPS_TO_COPY_FROM_SITE_BLOCK);

    block = _.merge(block, defaultProps);

    return block;
  }

  _prepareDesignPage(/** Object **/ data) {
    var result = {
      item: null,
    };

    var targetType = _.get(data, "targetDesignType");
    var page = _.get(data, "item");
    var isCurrentDesign = _.get(data, "isCurrentDesign");

    if (!isCurrentDesign) {
      page = this._preparePageBlocks(page);
    }

    switch (targetType) {
      case DESIGN_TYPE_PAGE:
        //clear out wordpress properties when creating a normal page
        result.item = WordpressRemover.removeFromPage(page);
        break;
      case DESIGN_TYPE_WP_PAGE:
        if (!_.has(page, "wp")) {
          _.set(page, "wp", {});
        }

        result.item = page;
        break;
    }

    return result;
  }

  _preparePageBlocks(/** Object **/ page) {
    _.forEach(
      page.blockData,
      _.bind(function (block, key) {
        if (_.has(block, BLOCK_PROP_PARENT_BLOCK_ID)) {
          page.blockData[key] = this._mergeSiteBlockIntoBlock(block);
        }
      }, this)
    );

    return page;
  }

  /**
   *
   * @param {Object} newPage
   * @param {Object} config
   * @param {SiteDesign} siteDesign
   * @returns
   */
  preparePage(newPage, config, siteDesign) {
    if (_.isEmpty(newPage) || _.has(config, "init")) {
      newPage = this._prepareBlankPage(config, siteDesign);
    }

    if (_.has(config, "toPage")) {
      newPage = WordpressRemover.removeFromPage(newPage);
    } else if (_.has(config, "toWP")) {
      newPage.wp = {};
    }

    [newPage] = this.cleanBlockContainer(newPage);

    return newPage;
  }

  /**
   *
   * @param {*} config
   * @param {*} siteDesign
   * @returns
   */
  _prepareBlankPage(config, siteDesign) {
    const newPage = ModelFactory.getPage();

    newPage.version = siteDesign.data.get("version", 1);
    newPage.mobile.w = siteDesign.data.getIn([LAYOUT_MOBILE, "w"]);
    newPage.desktop.w = siteDesign.data.getIn([LAYOUT_DESKTOP, "w"]);

    const newBlock = ModelFactory.getBlock();
    newBlock.id = nanoid(ID_LENGTH);

    const { theme } = siteDesign;
    const themedPage = applyThemeBG(newPage, theme);
    const themedBlock = applyThemeBG(newBlock, theme);

    themedPage.blocks.push(themedBlock.id);
    themedPage.blockData[themedBlock.id] = themedBlock;

    return themedPage;
  }

  /**
   * @param {Object} elementData
   * @param {SiteDesign} siteDesign
   * @param {Block | ElementContainer} container
   * @param {boolean} [maintainPosition]
   * @returns
   */
  prepareElement(elementData, siteDesign, container, maintainPosition) {
    const { theme } = siteDesign;
    const layouts = {
      mobile: {
        w: container.data.getIn([LAYOUT_MOBILE, "w"]),
        h: container.data.getIn([LAYOUT_MOBILE, "h"]),
      },
      desktop: {
        w: container.data.getIn([LAYOUT_DESKTOP, "w"]),
        h: container.data.getIn([LAYOUT_DESKTOP, "h"]),
      },
    };

    elementData.mobile.h = layouts.mobile.h * 0.7;
    elementData.mobile.w = layouts.mobile.w * 0.7;

    elementData.desktop.h = layouts.desktop.h * 0.4;
    elementData.desktop.w = layouts.desktop.w * 0.4;

    const elementType = elementData.type;

    switch (elementType) {
      case ELEMENT_TYPE_TEXT:
        this._prepareText(elementData, siteDesign);
        break;
      case ELEMENT_TYPE_ICON:
        this._prepareIcon(elementData);
        break;
      case ELEMENT_TYPE_BUTTON:
        this._prepareButton(elementData, theme);
        break;
      case ELEMENT_TYPE_FORM_INPUT:
      case ELEMENT_TYPE_FORM_SELECT:
      case ELEMENT_TYPE_FORM_RADIO:
      case ELEMENT_TYPE_FORM_CHECKBOX:
        this._prepareFormField(elementData);
        break;
      case ELEMENT_TYPE_LINE:
        this._prepareLine(elementData);
        break;
      case ELEMENT_TYPE_SOCIAL_GRID:
        this._prepareSocialGrid(elementData);
        break;
      case ELEMENT_TYPE_GRAPHIC:
      case ELEMENT_TYPE_SVG:
      case ELEMENT_TYPE_LOTTIE:
        this._prepareGraphic(elementData, layouts);
        break;
    }

    const defaultSyncProps = DEFAULT_MERGE_PROPS[elementType] || [];

    elementData.sync = [...defaultSyncProps];

    if (!maintainPosition) {
      elementData.mobile.x = Math.round(layouts.mobile.w / 2 - elementData.mobile.w / 2);
      elementData.mobile.y = Math.round(layouts.mobile.h / 2 - elementData.mobile.h / 2);

      elementData.desktop.x = Math.round(layouts.desktop.w / 2 - elementData.desktop.w / 2);
      elementData.desktop.y = Math.round(layouts.desktop.h / 2 - elementData.desktop.h / 2);
    }

    return elementData;
  }

  prepareGroup(elementsData) {
    const rects = LAYOUTS.reduce((result, layout) => {
      const layoutRects = [...elementsData.values()].map((element) =>
        TransformerStore.getElementBoundingRectangle(
          gatherProps(element, layout, PROPERTY_MAPS.ELEMENT_POSITION, false),
          element.get("id")
        )
      );

      const layoutRect = TransformerStore.getSelectionBoundingRectangle(layoutRects);
      result[layout] = {
        x: layoutRect.left,
        y: layoutRect.top,
        w: layoutRect.width,
        h: layoutRect.height,
        a: 0,
      };

      return result;
    }, {});

    return fromJS({
      [LAYOUT_DESKTOP]: rects[LAYOUT_DESKTOP],
      id: nanoid(AppConstants.ID_LENGTH),
      [LAYOUT_MOBILE]: rects[LAYOUT_MOBILE],
      [ELEMENTS]: {},
      [ELEMENT_ORDER]: [],
      hover: {
        triggerChildHovers: true,
      },
      type: DataTypes.GROUP,
      visible: "a",
    });
  }

  _prepareText(elementData, /** SiteDesign **/ siteDesign) {
    const style = siteDesign.data.getIn(["theme", "styles", elementData.desktop.style]);
    const desktopFontSize = style.getIn([LAYOUT_DESKTOP, "size"]);
    const mobileFontSize = style.getIn([LAYOUT_MOBILE, "size"], desktopFontSize);

    elementData.sync = [...DEFAULT_MERGE_PROPS.text];

    elementData.mobile.h = mobileFontSize + 5;
    elementData.desktop.h = desktopFontSize + 5;

    elementData.mobile.w = elementData.mobile.h * 0.6 * elementData.content.text.length;
    elementData.desktop.w = elementData.desktop.h * 0.6 * elementData.content.text.length;

    return elementData;
  }

  /**
   * This function checks the config for old googleAnalytics data and removes it from the JSON if it exists
   * @param {Object} data
   * @returns {[Object, boolean]} The object with the cleaned config and boolean flag
   */
  _prepareSite(data) {
    let dirty = false;
    const { config = {} } = data;
    if ("googleAnalytics" in config) {
      delete data.config["googleAnalytics"];
      dirty = true;
    }
    return [data, dirty];
  }

  _prepareIcon(elementData) {
    elementData.mobile.h = 50;
    elementData.mobile.w = 50;

    elementData.desktop.h = 100;
    elementData.desktop.w = 100;
  }

  /**
   *
   * @param {Object} elementData
   * @param {ImmutableMap} theme
   */
  _prepareButton(elementData, theme) {
    const themeObject = theme?.toJS?.() ?? undefined;

    elementData.sync = [...DEFAULT_MERGE_PROPS.button];

    elementData.mobile.h = ElementUtils.getMinHeight(elementData, LAYOUT_MOBILE, themeObject);
    elementData.desktop.h = ElementUtils.getMinHeight(elementData, LAYOUT_DESKTOP, themeObject);
    elementData.mobile.w = DEFAULT_BUTTON_MOBILE_WIDTH;
    elementData.desktop.w = DEFAULT_BUTTON_DESKTOP_WIDTH;
  }

  /**
   * Sizes a newly added form-field element.
   * @param {Object} elementData
   */
  _prepareFormField(elementData) {
    const { type, content, mobile, desktop } = elementData;

    mobile.w = FORM_FIELD_WIDTHS.mobile;
    desktop.w = FORM_FIELD_WIDTHS.desktop;

    if (type === ELEMENT_TYPE_FORM_CHECKBOX) {
      mobile.h = FORM_FIELD_CHECKBOX_HEIGHTS.mobile;
      desktop.h = FORM_FIELD_CHECKBOX_HEIGHTS.desktop;
      return;
    }

    const heights = content.multiLine ? FORM_FIELD_MULTILINE_HEIGHTS : FORM_FIELD_INPUT_HEIGHTS;
    mobile.h = heights.mobile;
    desktop.h = heights.desktop;
  }

  _prepareLine(elementData) {
    elementData.mobile.h = 3;
    elementData.desktop.h = 3;
  }

  _prepareSocialGrid(elementData) {
    elementData.content.allowVideo = 1;
    elementData.content.allowCarousel = 1;
    elementData.content.allowImage = 1;
    elementData.content.allowCaption = 1;
    elementData.mobile.rows = 1;
    elementData.desktop.rows = 1;
    elementData.mobile.gap = 5;
    elementData.desktop.gap = 5;
    elementData.mobile.h = 90;
    elementData.desktop.h = 125;
    elementData.mobile.w = 300;
    elementData.desktop.w = 1100;
    elementData.mobile.alignment = ALIGN_CENTER;
    elementData.desktop.alignment = ALIGN_CENTER;
  }

  _prepareGraphic(elementData, layouts) {
    var content = elementData.content;
    if (_.has(content, "aspect_ratio")) {
      var aspectRatio = content.aspect_ratio;

      elementData.desktop.w = Math.round(layouts.desktop.w * 0.7);
      elementData.desktop.h = Math.round(elementData.desktop.w / aspectRatio);

      elementData.mobile.w = Math.round(layouts.mobile.w * 0.7);
      elementData.mobile.h = Math.round(elementData.mobile.w / aspectRatio);

      if (elementData.desktop.h > layouts.desktop.h) {
        elementData.desktop.h = Math.round(layouts.desktop.h * 0.7);
        elementData.desktop.w = Math.round(elementData.desktop.h * aspectRatio);
      }

      if (elementData.mobile.h > layouts.mobile.h) {
        elementData.mobile.h = Math.round(layouts.mobile.h * 0.7);
        elementData.mobile.w = Math.round(elementData.mobile.h * aspectRatio);
      }
    } else {
      elementData.desktop.w = DEFAULT_IMAGE_WIDTH_DESKTOP;
      elementData.desktop.h = DEFAULT_IMAGE_HEIGHT_DESKTOP;
      elementData.mobile.w = DEFAULT_IMAGE_WIDTH_MOBILE;
      elementData.mobile.h = DEFAULT_IMAGE_HEIGHT_MOBILE;
    }

    return elementData;
  }

  /**
   * Removes any invalid (null or missing desktop property) elements from blocks or states and
   * removes any invalid (null, missing name) states. Returns dirty as true if a change is made.
   * @param {Object} blockContainer
   * @returns {[Object, boolean]} - cleaned block container
   */
  cleanBlockContainer(blockContainer) {
    let dirty = false;
    const blocks = _.get(blockContainer, "blockData", false);

    const isInvalid = (type) => (item) => {
      const invalid = item === null || !_.has(item, type === "state" ? "name" : LAYOUT_DESKTOP);
      invalid && (dirty = invalid);
      return invalid;
    };

    const validateElements = (elements) => {
      if (elements) {
        _.remove(elements, isInvalid());
        _.forEach(elements, (element) => _.unset(element, "selected"));
      }
    };

    if (blocks) {
      _.each(blocks, (block) => {
        const blockElements = _.get(block, "elements", false);
        validateElements(blockElements);

        if (blockElements) {
          _.forEach(blockElements, (element) => {
            if (this._inferBorderStyleValue(element)) dirty = true;
            if (this._migrateBorderSyncInElement(element)) dirty = true;
          });
        }

        const states = _.get(block, "states", false);
        if (states) {
          _.remove(states, isInvalid("state"));

          _.each(states, (state) => {
            const stateElements = _.get(state, "elements", false);
            validateElements(stateElements);
            if (stateElements) {
              _.forEach(stateElements, (element) => {
                if (this._inferBorderStyleValue(element)) dirty = true;
                if (this._migrateBorderSyncInElement(element)) dirty = true;
              });
            }
          });
        }
      });
    }

    return [blockContainer, dirty];
  }

  /**
   * Cleans canvas set list, then canvas set items, then canvas set data
   * @param {SiteDesign.data} design - SiteJson Object
   */
  // eslint-disable-next-line class-methods-use-this
  cleanCanvasSetData(design) {
    const { items = {}, canvasSets = [], canvasSetData = {}, blockData = {} } = design;

    const canvasSetDataKeys = Object.keys(canvasSetData);
    const siteCanvasKeys = Object.keys(blockData);

    // removes ids from canvasSetList if data doesn't exist
    const cleanedList = canvasSets.filter((canvasSetId) => canvasSetDataKeys.includes(canvasSetId));

    // removes invalid canvas set ids from siteItems
    const cleanedItems = Object.entries(items).reduce((result, [key, item]) => {
      const { canvasSet = [] } = item;
      // eslint-disable-next-line no-param-reassign
      result[key] = {
        ...item,
        canvasSet: canvasSet.filter((value) => value && cleanedList.includes(value)),
      };
      return result;
    }, {});

    // removes site canvas ids from children list if data doesn't exist
    const cleanedData = Object.entries(canvasSetData).reduce((result, [key, canvasSet]) => {
      const { children = [CANVAS_SET_BLOCK_CONTENT_SEPARATOR] } = canvasSet;
      const cleanedChildren = children.filter(
        (siteBlockId) =>
          siteBlockId === CANVAS_SET_BLOCK_CONTENT_SEPARATOR || siteCanvasKeys.includes(siteBlockId)
      );
      // eslint-disable-next-line no-param-reassign
      result[key] = { ...canvasSet, children: cleanedChildren };
      return result;
    }, {});

    return {
      ...design,
      items: cleanedItems,
      canvasSets: cleanedList,
      canvasSetData: cleanedData,
    };
  }

  /**
   * Converts a page block action type to a site block action type.
   * @param {string} actionType - The action type to convert
   * @returns {string} - The converted action type
   */
  // eslint-disable-next-line class-methods-use-this
  _convertActionTypeToSiteBlock(actionType) {
    if (!actionType || actionType === "") {
      return ELEMENT_ACTION_SHOW_SITE_BLOCK;
    }
    if (actionType === ELEMENT_ACTION_SHOW) {
      return ELEMENT_ACTION_SHOW_SITE_BLOCK;
    }
    if (actionType === ELEMENT_ACTION_HIDE) {
      return ELEMENT_ACTION_HIDE_SITE_BLOCK;
    }
    return actionType;
  }

  /**
   * Checks if an action needs conversion from page block to site block type.
   * @param {Object} action - The action to check
   * @returns {boolean} - True if action needs conversion
   */
  // eslint-disable-next-line class-methods-use-this
  _needsActionConversion(action) {
    if (!action) return false;
    const { type } = action;
    return !type || type === "" || type === ELEMENT_ACTION_SHOW || type === ELEMENT_ACTION_HIDE;
  }

  /**
   * Cleans elements by converting page block action types to site block action types.
   * @param {Array|Object} elements - Array or object of elements to clean
   * @returns {Array|Object} - Cleaned elements array or object
   */
  // eslint-disable-next-line class-methods-use-this
  cleanElements(elements) {
    if (!elements) {
      return elements;
    }

    if (Array.isArray(elements)) {
      return elements.map((element) => this._cleanElement(element));
    }

    if (this._isPlainObject(elements)) {
      const cleanedElements = {};
      let hasChanges = false;

      for (const [elementId, element] of Object.entries(elements)) {
        const cleanedElement = this._cleanElement(element);
        cleanedElements[elementId] = cleanedElement;
        if (cleanedElement !== element) {
          hasChanges = true;
        }
      }

      return hasChanges ? cleanedElements : elements;
    }

    return elements;
  }

  /**
   * Validates that an element has the structure needed for cleaning.
   * @param {Object} element - The element to validate
   * @returns {boolean} - True if element has valid structure for cleaning
   */
  // eslint-disable-next-line class-methods-use-this
  _hasValidElementStructure(element) {
    return element && element.pubs && Array.isArray(element.pubs.click);
  }

  /**
   * Checks if a value is a plain object (not array, not null).
   * @param {*} value - The value to check
   * @returns {boolean} - True if value is a plain object
   */
  // eslint-disable-next-line class-methods-use-this
  _isPlainObject(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }

  /**
   * Cleans a single element by converting page block action types to site block action types.
   * @param {Object} element - The element to clean
   * @returns {Object} - The cleaned element (or original if no cleaning needed)
   */
  _cleanElement(element) {
    if (!this._hasValidElementStructure(element)) {
      return element;
    }

    if (!this._elementNeedsCleaning(element)) {
      return element;
    }

    const cleanedClick = element.pubs.click.map((action) => {
      if (!action) return action;
      return { ...action, type: this._convertActionTypeToSiteBlock(action.type) };
    });

    return {
      ...element,
      pubs: {
        ...element.pubs,
        click: cleanedClick,
      },
    };
  }

  /**
   * Checks if an element has page block actions that need conversion.
   * @param {Object} element - The element to check
   * @returns {boolean} - True if element needs cleaning
   */
  // eslint-disable-next-line class-methods-use-this
  _elementNeedsCleaning(element) {
    if (!this._hasValidElementStructure(element)) {
      return false;
    }
    return element.pubs.click.some((action) => this._needsActionConversion(action));
  }

  /**
   * Checks if elements array or object has any elements that need cleaning.
   * @param {Array|Object} elements - Array or object of elements to check
   * @returns {boolean} - True if any element needs cleaning
   */
  // eslint-disable-next-line class-methods-use-this
  _elementsArrayOrObjectNeedsCleaning(elements) {
    if (!elements) {
      return false;
    }

    if (Array.isArray(elements)) {
      return elements.some((element) => this._elementNeedsCleaning(element));
    }

    if (this._isPlainObject(elements)) {
      return Object.values(elements).some((element) => this._elementNeedsCleaning(element));
    }

    return false;
  }

  /**
   * Cleans a state's elements if needed.
   * recursively cleans elements in the state.elements array or object.
   * @param {Object} state - The state to clean
   * @returns {Object} - The cleaned state
   */
  // eslint-disable-next-line class-methods-use-this
  _cleanState(state) {
    if (!state) {
      return state;
    }
    if (!this._elementsArrayOrObjectNeedsCleaning(state.elements)) {
      return state;
    }
    return { ...state, elements: this.cleanElements(state.elements) };
  }

  /**
   * Migrates legacy border sync on a single simple element (mutated in place).
   * - Both bW+bC synced without bS → promote to bS, strip bW/bC
   * - Partial sync (only bW or bC) without bS → materialize desktop→mobile, strip from sync
   * - If bS already present, orphaned bW/bC are simply stripped
   * @param {Object} element - Plain JS element
  /**
   * Ensures bS value exists on each layout when border props (bW/bC) are present.
   * Legacy Simple elements may have bW/bC without an explicit bS — infers "solid".
   * @param {Object} element - Plain JS element
   * @returns {boolean} true if element was changed
   */
  _inferBorderStyleValue(element) {
    if (!element || element.type !== DataTypes.SIMPLE) return false;
    let changed = false;

    const desktop = element.desktop || {};
    if (!desktop[BORDER_STYLE] && (desktop[BORDER_WIDTH] || desktop[BORDER_COLOR])) {
      desktop[BORDER_STYLE] = "solid";
      element.desktop = desktop;
      changed = true;
    }
    const mobile = element.mobile || {};
    if (!mobile[BORDER_STYLE] && (mobile[BORDER_WIDTH] || mobile[BORDER_COLOR])) {
      mobile[BORDER_STYLE] = "solid";
      element.mobile = mobile;
      changed = true;
    }
    return changed;
  }

  /**
   * Migrates legacy border sync on a single simple element (mutated in place).
   * - Both bW+bC synced without bS → promote to bS, strip bW/bC
   * - Partial sync (only bW or bC) without bS → materialize desktop→mobile, strip from sync
   * - If bS already present, orphaned bW/bC are simply stripped
   * @param {Object} element - Plain JS element
   * @returns {boolean} true if element was changed
   */
  _migrateBorderSyncInElement(element) {
    if (!element || element.type !== DataTypes.SIMPLE) return false;
    const sync = element.sync;
    if (!Array.isArray(sync)) return false;

    const hasBw = sync.includes(BORDER_WIDTH);
    const hasBc = sync.includes(BORDER_COLOR);
    const hasBs = sync.includes(BORDER_STYLE);
    if (!hasBw && !hasBc) return false;

    // Partial sync without bS: copy desktop→mobile so values aren't lost when stripped
    if (!hasBs && !(hasBw && hasBc)) {
      const desktop = element.desktop || {};
      const mobile = element.mobile || {};
      if (hasBw && desktop[BORDER_WIDTH] !== undefined && mobile[BORDER_WIDTH] === undefined) {
        mobile[BORDER_WIDTH] = desktop[BORDER_WIDTH];
      }
      if (hasBc && desktop[BORDER_COLOR] !== undefined && mobile[BORDER_COLOR] === undefined) {
        mobile[BORDER_COLOR] = desktop[BORDER_COLOR];
      }
      element.mobile = mobile;
    }

    // Strip bW/bC from sync; promote to bS if both were present
    element.sync = sync.filter((prop) => prop !== BORDER_WIDTH && prop !== BORDER_COLOR);
    if (!hasBs && hasBw && hasBc) element.sync.push(BORDER_STYLE);

    return true;
  }

  /**
   * Cleans a block by ensuring it has the correct pType property and converting
   * page block action types to site block action types in elements and state elements.
   * Primarily used to fix legacy site json data.
   * @param {Object} block - The block to clean
   * @returns {[Object, boolean]} - cleaned block and dirty flag
   */
  _cleanBlock(block) {
    if (!block) {
      return [block, false];
    }

    const needsPType = !_.has(block, "pType");
    const isSiteBlock = needsPType || block.pType === SITE_ITEM_SITE;

    let dirty = false;
    let updatedBlock = block;

    if (needsPType) {
      updatedBlock = Object.assign({}, block, { pType: SITE_ITEM_SITE });
      dirty = true;
    }

    if (!isSiteBlock) {
      return [updatedBlock, dirty];
    }

    const cleanedElements = this.cleanElements(updatedBlock.elements);
    if (cleanedElements !== updatedBlock.elements) {
      updatedBlock = { ...updatedBlock, elements: cleanedElements };
      dirty = true;
    }

    if (Array.isArray(updatedBlock.states)) {
      const cleanedStates = updatedBlock.states.map((state) => this._cleanState(state));
      if (cleanedStates.some((state, index) => state !== updatedBlock.states[index])) {
        updatedBlock = {
          ...updatedBlock,
          states: cleanedStates,
        };
        dirty = true;
      }
    }

    return [updatedBlock, dirty];
  }

  /**
   * Makes sure that all site blocks in blockData have the correct pType property.
   * Blocks in a site design's blockData should have pType set to SITE_ITEM_SITE.
   * Also converts page block action types to site block action types in elements
   * and state elements. Primarily used to fix legacy site json data.
   * @param {Object} design - SiteJson Object
   * @returns {[Object, boolean]} - cleaned design and dirty flag
   */
  // eslint-disable-next-line class-methods-use-this
  cleanSiteBlockData(design) {
    let dirty = false;

    const { blockData = {} } = design;
    if (!blockData || Object.keys(blockData).length === 0) {
      return [design, dirty];
    }

    const cleanedBlockData = Object.entries(blockData).reduce((result, [blockId, block]) => {
      const [cleanedBlock, blockDirty] = this._cleanBlock(block);

      if (blockDirty) {
        dirty = true;
      }
      result[blockId] = cleanedBlock;
      return result;
    }, {});

    if (!dirty) {
      return [design, dirty];
    }

    return [
      {
        ...design,
        blockData: cleanedBlockData,
      },
      dirty,
    ];
  }

  /**
   * Adds any form styles the theme is missing.
   * @param {DesignTheme} theme
   * @returns {[DesignTheme, boolean]} - the theme and whether styles were added
   */
  addMissingFormStyles(theme) {
    if (hasAllFormStyles(theme)) {
      return [theme, false];
    }

    return [createDefaultFormStyles(theme), true];
  }
}

export default new DesignPreparer();
